import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ChangeEvent, useRef } from 'react';
import { api } from '../../api/client';
import { errorMessageKey, unwrap } from '../../api/api-error';
import { ensureOk, ME_KEY, type Profile } from '../../auth/session';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Notice } from '../../components/ui/notice';
import { useI18n } from '../../i18n/i18n';

const ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,image/avif';
type AvatarType = 'image/jpeg' | 'image/png' | 'image/webp' | 'image/gif' | 'image/avif';

/** Аватар: файл уходит телом запроса как есть; сервер проверяет и перекодирует его сам. */
export function AvatarSection({ profile }: { profile: Profile }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: async (file: File) =>
      unwrap(
        await api.PUT('/api/v1/me/avatar', {
          // В OpenAPI тело двоичное (string/binary); отдаём сам файл без JSON-сериализации.
          body: file as unknown as string,
          bodySerializer: (body) => body as unknown as Blob,
          headers: { 'Content-Type': (file.type || 'application/octet-stream') as AvatarType },
        }),
      ),
    onSuccess: (updated) => queryClient.setQueryData(ME_KEY, updated),
  });
  const remove = useMutation({
    mutationFn: async () => ensureOk(await api.DELETE('/api/v1/me/avatar')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ME_KEY }),
  });

  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file !== undefined) upload.mutate(file);
    event.target.value = '';
  };

  const initial = profile.name.trim().charAt(0).toUpperCase() || '?';
  return (
    <Card title={t('profile.avatar.title')}>
      <div className="flex items-center gap-4">
        {profile.avatarUrl === null ? (
          <span className="grid size-16 place-items-center rounded-full bg-tint text-xl font-semibold text-muted-foreground">
            {initial}
          </span>
        ) : (
          <img src={profile.avatarUrl} alt="" className="size-16 rounded-full object-cover" />
        )}
        <input ref={input} type="file" accept={ACCEPT} className="hidden" onChange={onFile} />
        <Button
          type="button"
          variant="secondary"
          onClick={() => input.current?.click()}
          disabled={upload.isPending}
        >
          {t('profile.avatar.upload')}
        </Button>
        {profile.avatarUrl !== null && (
          <Button
            type="button"
            variant="link"
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
          >
            {t('profile.avatar.remove')}
          </Button>
        )}
      </div>
      <p className="text-2xs text-muted-foreground">{t('profile.avatar.hint')}</p>
      {upload.isError && <Notice tone="danger">{t(errorMessageKey(upload.error))}</Notice>}
      {remove.isError && <Notice tone="danger">{t(errorMessageKey(remove.error))}</Notice>}
    </Card>
  );
}
